import React, { useState } from 'react'
import {
  Avatar,
  Button,
  Grid,
  Link,
  makeStyles,
  TextField,
  TextFieldProps,
  Typography,
} from '@material-ui/core'
import { LockIcon } from '../icons'
import { useForm } from 'react-hook-form'
import { SignupInfo, useSignup } from '../api'
import { strings } from '../strings'
import { useCaptcha } from '../Captcha'
import { Link as RouterLink } from 'react-router-dom'
import { signedIn } from '../store/slices/auth-slice'
import { useDispatch } from 'react-redux'
import { PasswordField } from '../PasswordField'
import { iranCities, iranStates, parseNationalCode } from '../national-code'

export function SignUp() {
  const classes = useStyles()
  const Signup = useSignup()
  const dispatch = useDispatch()
  const { register, handleSubmit, errors, setValue, getValues } = useForm<SignupInfo>()
  const [message, setMessage] = useState({ error: false, text: '' })
  const [captcha, refreshCaptcha, getCaptchaInfo] = useCaptcha()
  const [state, setState] = useState('')
  const [city, setCity] = useState('')

  const onSubmit = (formData: SignupInfo) => {
    const captchaInfo = getCaptchaInfo()

    Signup({ ...formData, username: formData.nationalCode }, captchaInfo)
      .then((token) => {
        dispatch(signedIn(token))
      })
      .catch((err) => {
        setMessage({ error: true, text: err.message })
        refreshCaptcha()
      })
  }

  const updateStateBasedOnNationalCode = () => {
    if (getValues().state.length > 0) return
    const nc = parseNationalCode(getValues().nationalCode)
    if (!nc.valid) return
    setValue('state', nc.state)
    setState(nc.state)
  }

  const updateCityBasedOnNationalCode = () => {
    if (getValues().city.length > 0) return
    const nc = parseNationalCode(getValues().nationalCode)
    if (!nc.valid) return
    setValue('city', nc.city)
    setCity(nc.state)
  }

  return (
    <div className={classes.paper}>
      <Avatar className={classes.avatar}>
        <LockIcon />
      </Avatar>
      <Typography component="h1" variant="h5">
        ثبت نام
      </Typography>
      <form className={classes.form} noValidate onSubmit={handleSubmit(onSubmit)}>
        <CustomTextField
          name="fullname"
          label={strings.fullname}
          autoFocus
          inputRef={register({ required: true })}
          error={!!errors.fullname}
        />
        <CustomTextField
          name="nationalCode"
          label={strings.nationalCode}
          inputRef={register({ required: true, validate: (v) => parseNationalCode(v).valid })}
          error={!!errors.nationalCode}
        />
        <CustomTextField
          name="state"
          label={strings.state}
          inputRef={register({ required: true })}
          InputLabelProps={state.length > 0 ? { shrink: true } : {}}
          error={!!errors.state}
          inputProps={{ list: 'datalist-states' }}
          onChange={(ev) => setState(ev.target.value)}
          onFocus={updateStateBasedOnNationalCode}
        />
        <datalist id="datalist-states">
          {iranStates.map((x) => (
            <option key={x}>{x}</option>
          ))}
        </datalist>
        <CustomTextField
          name="city"
          label={strings.city}
          inputRef={register({ required: true })}
          error={!!errors.city}
          InputLabelProps={city.length > 0 ? { shrink: true } : {}}
          onChange={(ev) => setCity(ev.target.value)}
          inputProps={{ list: 'datalist-cities' }}
          onFocus={updateCityBasedOnNationalCode}
        />
        <datalist id="datalist-cities">
          {iranCities(state).map((x) => (
            <option key={x}>{x}</option>
          ))}
        </datalist>
        <CustomTextField
          name="phoneNumber"
          label={strings.phoneNumber}
          inputRef={register({ required: true, pattern: /^09\d{9}$/ })}
          error={!!errors.phoneNumber}
        />
        <PasswordField
          name="password"
          label={strings.password}
          inputRef={register({
            required: true,
            minLength: { value: 8, message: strings.passwordMinLengthHelper },
            pattern: {
              value:
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\[\]{};':"\\|,.<>\/?`~\-]).{8,}$/,
              message: strings.passwordFormatHelper,
            },
          })}
          error={!!errors.password}
          helperText={errors.password?.message}
          margin="normal"
          autoComplete="off"
          variant="outlined"
          fullWidth
        />
        {captcha}
        <Typography color={message.error ? 'error' : 'primary'}>{message.text}</Typography>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.submit}
        >
          ثبت نام
        </Button>
        <Grid container>
          <Grid item xs>
            &nbsp;
          </Grid>
          <Grid item>
            <Link component={RouterLink} to="/auth/signin">
              حساب دارید؟ ورود
            </Link>
          </Grid>
        </Grid>
      </form>
    </div>
  )
}

function CustomTextField(props: TextFieldProps) {
  return <TextField margin="normal" variant="outlined" fullWidth {...props} />
}

const useStyles = makeStyles((theme) => ({
  paper: {
    margin: theme.spacing(8, 4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}))
